// Document grid. Each tile shows extension, title, type, date and, once
// the AI has read it, how many of the asked-for fields it found. Filter chips
// across the top map to MatterDocument.type. Empty state shown when the
// matter has no documents (the realistic case for fresh Mongo records).
//
// A tile opens what it points at: the Drive file when the record names
// one, otherwise the bytes the server holds. Everything else used to go
// to a Drive name-search, which finds nothing for a file the firm holds.

import { useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { MatterDocument } from "../../data/types";
import { caseSignalsKey } from "../../hooks/useCaseSignals";
import { documentTypeLabel } from "../../lib/caseLabels";
import {
  downloadDocument,
  setDocumentPublished,
  uploadCaseDocument,
  UPLOAD_ACCEPT,
  uploadProblem,
} from "../../api/documents";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";
import { SEP } from "../chrome/SepList";
import { DriveFolderDialog } from "./DriveFolderDialog";

interface Props {
  documents: MatterDocument[] | undefined;
  /** Mongo _id of the Case; absent for fixture matters, which cannot upload. */
  caseId?: string;
  /** Set when the case is linked to a Drive folder - what the folder holds
      is then a question worth asking, and only an admin may ask it. */
  driveFolder?: string;
}

interface MatterDocumentMaybeLinked extends MatterDocument {
  // Optional fields that some doc records carry today. Reading them
  // permissively lets the tile open the live Drive file when present and
  // gracefully fall back when not.
  driveUrl?: string;
  driveId?: string;
  url?: string;
}

function docOpenUrl(d: MatterDocumentMaybeLinked): string | null {
  if (d.driveUrl) return d.driveUrl;
  if (d.url) return d.url;
  if (d.driveId) return `https://drive.google.com/file/d/${encodeURIComponent(d.driveId)}/view`;
  return null;
}

// The tile face, whether it ends up a link, a button, or neither.
const FACE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  flex: 1,
  textDecoration: "none",
  color: "inherit",
  textAlign: "left",
  background: "none",
  border: 0,
  padding: 0,
  font: "inherit",
};

const FILTER_ALL = "All";
// The chip for files with no type on record - not read yet, or read without a
// type the classifier or the model could name. "Other" would be a verdict.
// The key is internal; no server code looks like it.
const NO_TYPE = "?";

function chipLabel(key: string): string {
  return key === NO_TYPE ? "Type not known" : documentTypeLabel(key);
}

// What the server's status means to someone at a desk. "extracted" is the
// only one with nothing to say - the field count beside the title says it.
const STATUS_COPY: Record<string, string> = {
  uploaded: "Not read yet",
  parsing: "Being read...",
  parsed: "Text read, nothing extracted",
  failed: "Could not be read",
  unreadable: "Nothing to read in this file",
  unmatched: "Not linked to a matter",
};

// A failure may need someone to act, so it is red; a file with nothing in
// it is a fact about the file, not an alarm. Both carry the server's reason.
const STOPPED_SHORT = new Set(["failed", "unreadable"]);

function extLabel(ext: string): string {
  return (ext || "").toUpperCase().slice(0, 4);
}

export function DocWall({ documents, caseId, driveFolder }: Props) {
  const [filter, setFilter] = useState<string>(FILTER_ALL);
  const [override, setOverride] = useState<Record<string, boolean>>({});
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [driveOpen, setDriveOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const canReadDrive = isAdminRole(useCurrentUser()?.role) && Boolean(caseId && driveFolder);

  const uploadMut = useMutation({
    mutationFn: (file: File) => uploadCaseDocument(caseId as string, file),
    onSuccess: async () => {
      setUploadError(null);
      // A new paper can answer a signal - the card is re-read with the wall.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["matter"] }),
        queryClient.invalidateQueries({ queryKey: caseSignalsKey(caseId as string) }),
      ]);
    },
    onError: (err) => setUploadError(err instanceof Error ? err.message : "Upload failed."),
  });

  const startUpload = (file: File | undefined | null) => {
    if (!file || !caseId) return;
    const problem = uploadProblem(file);
    if (problem) {
      setUploadError(problem);
      return;
    }
    setUploadError(null);
    uploadMut.mutate(file);
  };

  const dropOverride = (id: string) =>
    setOverride((o) => {
      const next = { ...o };
      delete next[id];
      return next;
    });

  // Optimistic: the tile flips at once, and the override is dropped once the
  // refetched matter carries the new value - or straight away if the server
  // refused. Keeping it any longer would show a stale flag after someone
  // else changed the same document.
  const publish = useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) =>
      setDocumentPublished(id, published),
    onMutate: ({ id, published }: { id: string; published: boolean }) => {
      setOverride((o) => ({ ...o, [id]: published }));
    },
    onError: (_err, { id }) => dropOverride(id),
    onSuccess: async (_data, { id }) => {
      await queryClient.invalidateQueries({ queryKey: ["matter"] });
      dropOverride(id);
    },
  });

  const docs = documents ?? [];

  const types = Array.from(new Set(docs.map((d) => d.type ?? NO_TYPE))).sort((a, b) =>
    a === NO_TYPE ? 1 : b === NO_TYPE ? -1 : chipLabel(a).localeCompare(chipLabel(b), "en"),
  );
  const filters = [FILTER_ALL, ...types];
  // The chosen chip can vanish once a reading types the last file under it.
  const active = filters.includes(filter) ? filter : FILTER_ALL;
  const visible = active === FILTER_ALL ? docs : docs.filter((d) => (d.type ?? NO_TYPE) === active);
  const aiTouched = docs.filter((d) => d.aiConfidence != null).length;

  return (
    <div className="cc-card" style={{ marginBottom: 14 }}>
      <div className="cc-card-head">
        <div className="cc-card-title">Documents</div>
        <div className="cc-card-sub">
          {SEP}
          {docs.length} doc{docs.length === 1 ? "" : "s"}
          {aiTouched > 0 && `${SEP}${aiTouched} read by AI`}
        </div>
        {caseId && (
          <div className="actions">
            <input
              ref={fileInput}
              type="file"
              accept={UPLOAD_ACCEPT}
              style={{ display: "none" }}
              aria-label="Upload document"
              onChange={(e) => {
                startUpload(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <button
              className="cc-link-btn"
              type="button"
              disabled={uploadMut.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {uploadMut.isPending ? "Uploading..." : "Upload"}
            </button>
            {canReadDrive && (
              <button className="cc-link-btn" type="button" onClick={() => setDriveOpen(true)}>
                Drive folder
              </button>
            )}
          </div>
        )}
      </div>

      {(uploadError ?? openError) && (
        <div style={{ padding: "10px 18px 0", fontSize: 11.5, color: "var(--red-700)" }}>
          {uploadError ?? openError}
        </div>
      )}

      {canReadDrive && (
        <DriveFolderDialog
          caseId={caseId as string}
          open={driveOpen}
          onClose={() => setDriveOpen(false)}
        />
      )}

      {docs.length === 0 ? (
        <div
          onDragOver={caseId ? (e) => e.preventDefault() : undefined}
          onDrop={
            caseId
              ? (e) => {
                  e.preventDefault();
                  const files = e.dataTransfer.files;
                  if (files && files.length > 1) {
                    setUploadError("Drop one file at a time.");
                    return;
                  }
                  startUpload(files?.[0]);
                }
              : undefined
          }
          style={{
            padding: "24px 18px",
            color: "var(--muted)",
            fontSize: 12.5,
            textAlign: "center",
          }}
        >
          {caseId ? "No documents yet. Use Upload or drag a file here." : "No documents yet."}
        </div>
      ) : (
        <>
          {filters.length > 2 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                padding: "10px 18px 0",
              }}
            >
              {filters.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  aria-pressed={active === f}
                  style={{
                    padding: "3px 10px",
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    border: `1px solid ${active === f ? "var(--olive)" : "var(--line)"}`,
                    background: active === f ? "var(--olive)" : "var(--bg-elev)",
                    color: active === f ? "#fff" : "var(--muted)",
                  }}
                >
                  {f === FILTER_ALL ? f : chipLabel(f)}
                </button>
              ))}
            </div>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: 10,
              padding: "12px 18px 16px",
            }}
          >
            {visible.map((d) => {
              const conf = d.aiConfidence;
              const fields = d.aiFields;
              const linked = docOpenUrl(d as MatterDocumentMaybeLinked);
              // Only a document the server holds has bytes to fetch:
              // publishedToClient is what the adapter always sets and a
              // prototype fixture never carries.
              const stored = d.publishedToClient !== undefined;

              // One face, three wrappers: a drive link, a button that pulls
              // the bytes, or nothing to click at all.
              const face = (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 9.5,
                        fontWeight: 700,
                        padding: "2px 5px",
                        borderRadius: 3,
                        background: "var(--line-2)",
                        color: "var(--muted)",
                      }}
                    >
                      {extLabel(d.ext)}
                    </span>
                    {fields && (
                      // How much of the document came back, not how sure
                      // anything is: the confidence beside it is pass/fail
                      // on a schema whose fields are all optional, and it
                      // used to render here as a flat "100%".
                      <span
                        title={`${fields.found} of ${fields.expected} fields the AI was asked to find`}
                        style={{
                          fontVariantNumeric: "tabular-nums",
                          fontSize: 9.5,
                          fontWeight: 700,
                          padding: "2px 5px",
                          borderRadius: 3,
                          background: "var(--line-2)",
                          color: "var(--muted)",
                        }}
                      >
                        {fields.found}/{fields.expected} fields
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 550,
                      color: "var(--ink)",
                      lineHeight: 1.35,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {d.title}
                  </div>
                  {d.subfolder && (
                    <div
                      title={`Filed under ${d.subfolder} in the matter's Drive folder`}
                      style={{
                        fontSize: 10,
                        color: "var(--muted)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {d.subfolder}
                    </div>
                  )}
                  {d.status && d.status !== "extracted" && (
                    <div
                      title={d.statusReason}
                      style={{
                        fontSize: 11,
                        lineHeight: 1.35,
                        color: d.status === "failed" ? "var(--red-700)" : "var(--muted)",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {STATUS_COPY[d.status] ?? d.status}
                      {STOPPED_SHORT.has(d.status) && d.statusReason ? ` - ${d.statusReason}` : ""}
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: "auto",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 6,
                      fontSize: 10,
                      color: "var(--muted)",
                    }}
                  >
                    <span
                      title={documentTypeLabel(d.type) || undefined}
                      style={{
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {documentTypeLabel(d.type)}
                    </span>
                    <span className="tnum" style={{ whiteSpace: "nowrap" }}>
                      {d.date}
                    </span>
                  </div>
                </>
              );
              // A file the client sent is always theirs to see, whatever the
              // publish flag says - see CLIENT_VISIBLE_DOCS on the server.
              const ownUpload = d.source === "client_upload";
              // Work product never reaches the client, so the toggle is a
              // notice, not a control - the server refuses the publish anyway.
              const memo = d.workProduct === true;
              const shared = ownUpload
                ? true
                : memo
                  ? false
                  : (override[d.id] ?? d.publishedToClient);
              return (
                <div
                  key={d.id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    padding: 12,
                    borderRadius: 8,
                    background: "var(--bg)",
                    border: "1px solid var(--line)",
                    borderLeft: conf != null ? "2px solid var(--muted-2)" : "2px solid var(--line)",
                    minHeight: 96,
                  }}
                >
                  {linked ? (
                    <a
                      href={linked}
                      target="_blank"
                      rel="noreferrer noopener"
                      style={{ ...FACE, cursor: "pointer" }}
                      title={`Open ${d.title} in Drive`}
                    >
                      {face}
                    </a>
                  ) : stored ? (
                    <button
                      type="button"
                      onClick={() => {
                        setOpenError(null);
                        downloadDocument(d.id, d.ext ? `${d.title}.${d.ext}` : d.title).catch(
                          (err: Error) => setOpenError(err.message),
                        );
                      }}
                      style={{ ...FACE, cursor: "pointer" }}
                      title={`Open ${d.title}`}
                    >
                      {face}
                    </button>
                  ) : (
                    <div style={FACE} title="Prototype data - there is no file behind this one">
                      {face}
                    </div>
                  )}
                  {shared !== undefined && (
                    <button
                      type="button"
                      onClick={() => publish.mutate({ id: d.id, published: !shared })}
                      disabled={
                        ownUpload || memo || (publish.isPending && publish.variables?.id === d.id)
                      }
                      title={
                        ownUpload
                          ? "The client uploaded this file, so they can always see it."
                          : memo
                            ? "Attorney work product from 09_Memos. It never reaches the client portal."
                            : shared
                              ? "Published to the client portal. Click to make it internal."
                              : "Internal only. Click to publish it to the client portal."
                      }
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 10,
                        padding: "4px 6px",
                        borderRadius: 6,
                        border: "1px solid var(--line)",
                        background: "transparent",
                        cursor: ownUpload || memo ? "default" : "pointer",
                        fontSize: 9.5,
                        fontWeight: 700,
                        color: shared ? "var(--ok-600)" : "var(--muted)",
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: 999,
                          background: shared ? "var(--ok-600)" : "var(--muted-2)",
                        }}
                      />
                      {ownUpload
                        ? "From client"
                        : memo
                          ? "Work product"
                          : shared
                            ? "Published to portal"
                            : "Internal only"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {publish.isError && (
            <div
              style={{
                padding: "0 18px 14px",
                fontSize: 11.5,
                color: "var(--red-700)",
              }}
            >
              Could not change the client visibility of{" "}
              {docs.find((d) => d.id === publish.variables?.id)?.title ?? "the document"}. Try
              again.
            </div>
          )}
        </>
      )}
    </div>
  );
}
